import { usePage } from "@inertiajs/react"
import { useShotPanelStore } from "@/stores/shot-panel-store"
import type {
  GenerationModelsByContext,
  ProjectPermissions,
  ProjectSummary,
  WorkspaceBaseProps,
} from "@/types/workspace"

export type ShotPanelWorkspace = {
  project: ProjectSummary | null
  project_permissions: ProjectPermissions | null
  generation_models_by_context: GenerationModelsByContext | undefined
  sketch: WorkspaceBaseProps["sketch"] | undefined
  /** True when page props lack this project and bootstrap has not arrived yet. */
  bootstrapPending: boolean
}

/**
 * Workspace context for ShotPanel: prefer Inertia page props when they match the
 * open panel's projectId; otherwise use bootstrap fetched for non-workspace pages
 * (e.g. platform monitoring generations).
 */
export function useShotPanelWorkspace(): ShotPanelWorkspace {
  const page = usePage<Partial<WorkspaceBaseProps>>()
  const projectId = useShotPanelStore((s) => s.projectId)
  const bootstrap = useShotPanelStore((s) => s.workspaceBootstrap)

  const pageProject = page.props.project
  if (projectId != null && pageProject?.id === projectId) {
    return {
      project: pageProject,
      project_permissions: page.props.project_permissions ?? null,
      generation_models_by_context: page.props.generation_models_by_context,
      sketch: page.props.sketch,
      bootstrapPending: false,
    }
  }

  if (bootstrap && bootstrap.project.id === projectId) {
    return {
      project: bootstrap.project,
      project_permissions: bootstrap.project_permissions,
      generation_models_by_context: bootstrap.generation_models_by_context,
      sketch: bootstrap.sketch,
      bootstrapPending: false,
    }
  }

  return {
    project: null,
    project_permissions: null,
    generation_models_by_context: undefined,
    sketch: undefined,
    bootstrapPending: projectId != null,
  }
}
